import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphViewOverlay } from '../../pages/SocialGraphPOC/GraphViewOverlay';
import Tooltip from '../Tooltip/Tooltip';
import { GraphWidget } from '../GraphWidget/GraphWidget';
import { useSavedGraph } from '../GraphWidget/useSavedGraph';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { API, ShareAPI } from '../../API';
import { ampli } from '../../ampli';
import { ReactComponent as DiscoverIcon} from '../../assets/discover.svg';
import { SummaryPanel } from '../SummaryPanel/SummaryPanel';
import { SentimentBar } from '../SentimentBar/SentimentBar';
import { ActorsTypes } from '../ActorsTypesWidget/ActorsTypes';
import { TopActorsWidget } from '../TopActorsWidget/TopActorsWidget';
import { trendData } from './evolution';
import { OpinionsWidget } from '../OpinionsWidget/OpinionsWidget';
import { GeoChart } from '../GeoChart/GeoChart';
import { Loader } from '../Loader/Loader';
import {ReactComponent as NotFoundIcon} from '../../assets/notFound.svg';
import { AttitudeBar } from '../AttitudeBar/AttitudeBar';
import { ThreatEditModal } from '../../pages/ThreatsPage/ThreatEditModal';
import { ReactComponent as ThreatIcon } from '../../assets/shareThreat.svg';
import { ReactComponent as WarningIcon } from '../../assets/alertSign.svg';
import Modal from '../Modal';
import { toast } from 'react-toastify';
import { showRequestError } from '../../utils/requestError';

import styles from './StoryContent.module.scss';
import { EvolutionChart } from '../EvolutionChart/EvolutionChart';


export const StoryContent = ({
  report,
  story,
  isShare,
  isData=true,
  sourceGroups,
  countries,
  countryGroups,
  sentiment,
  startDate,
  endDate,
  isDateFiltered=false,
  scrollRef,
  isAttitude=false,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const showGraphWidget = currentUser?.workspace?.config?.featureflags?.includes('social_graph');
  const graphWidget = useSavedGraph('topic', story?.id, Boolean(showGraphWidget));
  // graph opens in an overlay (not a route) so closing it keeps the user's
  // place on this page — scroll position, tabs, filters
  const [isGraphOverlayOpen, setIsGraphOverlayOpen] = useState(false);
  const descriptionRef = useRef(null);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [storyData, setStoryData] = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [opinions, setOpinions] = useState(null);
  const [modal, setModal] = useState({isVisible: false, opinion: null});
  const [isThreatModalOpen, setIsThreatModalOpen] = useState(false);
  const [isSaveCaseModalOpen, setIsSaveCaseModalOpen] = useState(false);

  const newDescription = story?.description
    ?.replace(/\n+(\s*\n+)?/g, '<br/>')
    .split('<br/>')
    .map(item => DOMPurify.sanitize(item, { ALLOWED_TAGS: ['mark', 'span', 'br'], ALLOWED_ATTR: ['class'] }));

  const widgetConfig = report?.widget_config || {};
  
  const scrollToWidget = () => {
    scrollRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest', passive: true});
  }

  const api = isShare ? ShareAPI : API;
  const [storyShares, setStoryShares] = useState(trendData[story?.id]);

  const fetchStory = () => {
    if (!story) return;
    setStoryData(null);
    api.fetch('GET', `/API/v1/topic-modeling/${report?.id}/events/${story?.id}`).then((data) => {
      setStoryData(data);
      if(descriptionRef?.current !== null) {
        scrollToWidget();
      }
    }).catch((e) => showRequestError(e, t('Story')));
  };

  useEffect(() => {
    fetchStory();
  }, [story]);

  const fetchSentiment = () => {
    if (!story) return;
    const urlParams = new URLSearchParams();
    if(sourceGroups.length > 0) {
      sourceGroups.forEach((sourceGroup) => {
        urlParams.append('source_group_ids', sourceGroup.value);
      });
    }
    if(countries.length > 0) {
      countries.forEach((country) => {
        urlParams.append('source_country_ids', country.value);
      });
    }
    if(countryGroups.length > 0) {
      countryGroups.forEach((groupId) => {
        urlParams.append('country_group_ids', groupId);
      });
    }
    if(startDate) {
      urlParams.append('date_from',format(startDate, 'yyyy-LL-dd'));
    }
    if(endDate) {
      urlParams.append('date_to',format(endDate, 'yyyy-LL-dd'));
    }
    api.fetch('GET', `/API/v1/topic-modeling/${report?.id}/events/${story?.id}/sentiment?${urlParams.toString()}`).then((data) => {
      setSentimentData(data);
    }).catch((e) => showRequestError(e, t('Sentiment')));
  };

  useEffect(() => {
    fetchSentiment();
  }, [story]);

  const fetchStoryShares = (story, statsAggregation, statsBreakdown) => {
    if (!story) return;
    setStoryShares(null);
    const urlParams = new URLSearchParams();
    if (statsAggregation) {
      urlParams.set('aggregation', statsAggregation);
    }
    urlParams.set('cumulative', false)
    urlParams.set('top', 6)

    if(sourceGroups.length > 0) {
      sourceGroups.forEach((sourceGroup) => {
        urlParams.append('source_group_ids', sourceGroup.value);
      });
    }
    if(countries.length > 0) {
      countries.forEach((country) => {
        urlParams.append('source_country_ids', country.value);
      });
    }
    if(countryGroups.length > 0) {
      countryGroups.forEach((groupId) => {
        urlParams.append('country_group_ids', groupId);
      });
    }
    if(startDate) {
      urlParams.append('date_from',format(startDate, 'yyyy-LL-dd'));
    }
    if(endDate) {
      urlParams.append('date_to',format(endDate, 'yyyy-LL-dd'));
    }

    let url = `/API/v1/topic-modeling/${report.id}/events/${story.id}/messages/stats?${urlParams.toString()}`;

    if (statsBreakdown) {
      urlParams.set('breakdown', statsBreakdown);
      url = `/API/v1/topic-modeling/${report.id}/events/${story.id}/messages/stats?${urlParams.toString()}`;
    }

    api.fetch('GET', url)
    .then((data) => {
      setStoryShares(data);
    })
    .catch((e) => showRequestError(e, t('Evolution chart')));
  }

  useEffect(() => {
    if (Object.keys(widgetConfig)?.length === 0 || widgetConfig?.evolution_chart) {
      fetchStoryShares(story, 'DAY', null)
    }
  }, [story])

  const handleEvolutionChartChange = (aggregation, breakdown) => {
    fetchStoryShares(story, aggregation, breakdown)
  }

  const hasFilters = (sourceGroups?.length > 0) || (countries?.length > 0) || (countryGroups?.length > 0) || (sentiment?.length > 0) || isDateFiltered;

  const handleCreateCase = () => {
    api.fetch('POST', `/API/v1/topic-modeling/${report.id}/events/${story.id}/to_case`)
    .then((data) => {
      window.open(`/narratives/${data}`, '_blank');
    }
    ).catch((error) => {
      showRequestError(error, t('Create case'));
    });
  };

  useEffect(() => {
    ampli.track({
      event_type: 'Open event',
      event_properties: {
        user_id: currentUser?.id,
        workspace_id: currentUser?.workspace_id,
        report_id: report?.id,
      }
    });
  }, []);

  useEffect(() => {
    if (descriptionRef?.current !== null) {
      scrollToWidget();
    }
  }, [descriptionRef, storyData]);

  if(!isData){
    return (
      <div className={styles.noDataContainer}>
        <NotFoundIcon />
      </div>
    )
  }

  if (!story || !storyData) {
    return <Loader />;
  }

  const saveToThreat = (
    narrativeId,
    threatName,
    threatDescription,
    caseType,
    incidentType,
    topics,
    geoScope,
    narratives,
    campaigns,
    counterEntities,
    reviewStatus,
    declineReason,
    organizations,
    isCreatedByUcbiReporter,
    threatPublicNameEn,
    threatPublicNameUk,
    threatPublicDescriptionEn,
    threatPublicDescriptionUk,
    threatIsPublic,
    messages,
  ) => {
    const messagesList = Object.keys(messages);

    let data = {
      name: threatName,
      description: threatDescription,
      is_threat: true,
      public_name_en: threatPublicNameEn,
      public_name_uk: threatPublicNameUk,
      public_description_en: threatPublicDescriptionEn,
      public_description_uk: threatPublicDescriptionUk,
      is_public: threatIsPublic,
    };

    const headers = { 'Content-Type': 'application/json' };

    if (caseType) {
      data.type = caseType.value;
    }

    if (incidentType) {
      data.incident_type = incidentType.map((v) => v.value);
    }

    if (topics) {
      data.topic = topics.map((v) => v.value);
    }

    if (geoScope) {
      data.geo_scope = geoScope.map((v) => v.value);
    }

    if (narratives) {
      data.narrative_ids = narratives.map((v) => v.value);
    }

    if (campaigns) {
      data.campaign_ids = campaigns.map((v) => v.value);
    }

    if (counterEntities) {
      data.counter_entity_ids = counterEntities.map((v) => v.value);
    }

    if (reviewStatus) {
      data.review_status = reviewStatus;
    }

    if (declineReason) {
      data.decline_reason = declineReason;
    }

    if (organizations) {
      data.workspace_ids = organizations.map((v) => v.value);
    }

    data.is_created_by_ucbi_reporter = isCreatedByUcbiReporter;

    let body = {
      new_narrative: data,
      existing_narrative_id: null,
      event_ids: messagesList,
    };

    API.fetch(
      'POST',
      `/API/v1/topic-modeling/${narrativeId}/copy_from_events`,
      null,
      body,
    ).then(data => {
      window.open(`/threats/${data.id}`, '_blank');
    }).catch((error) => {
      showRequestError(error, t('Save threat'));
    });
  };


  return (
    <div className={styles.storyContentContainer}>
      <div ref={descriptionRef} className={styles.storyDescriptionContainer}>
        <div className={styles.storyDescriptionHeader}>
          <h3 className={styles.storyTitle}>{story?.title}</h3>
          {!isShare && <div className={styles.storyDescriptionControls}>
            <button
              type="button"
              className={styles.saveThreatButton}
              onClick={() => {
                setIsThreatModalOpen(true);
                ampli.track({
                  event_type: 'Share event as threat',
                  event_properties: {
                    user_id: currentUser?.id,
                    workspace_id: currentUser?.workspace_id,
                    report_id: report?.id,
                  },
                });
              }}
            >
              <ThreatIcon />
              {t('Save as threat')}
            </button>
            <button
              type="button"
              className={styles.saveCaseButton}
              onClick={() => {
                setIsSaveCaseModalOpen(true);
                ampli.track({
                  event_type: 'Save event to case',
                  event_properties: {
                    user_id: currentUser?.id,
                    workspace_id: currentUser?.workspace_id,
                    report_id: report?.id,
                  },
                });
              }}
            >
              <DiscoverIcon />
              {t('Save as case')}
            </button>
          </div>}
        </div>
        <div className={styles.storyContent}>
          {newDescription.map((item, index) => (
            <p
              key={`description_paragraph_${index}`}
              dangerouslySetInnerHTML={{ __html: item }}
            ></p>
          ))}
        </div>
      </div>

      <div className="report-section">
        <h3>{t('Summary')}</h3>
        <SummaryPanel
          report={report}
          narrativeIds={[storyData?.id]}
          isShare={isShare}
          isStory={true}
          sourceGroups={sourceGroups}
          countries={countries}
          countryGroups={countryGroups}
          startDate={startDate}
          endDate={endDate}
          summaryPanelType={isAttitude ? 'attitude' : widgetConfig?.summary_panel || 'standard'}
        />
      </div>

      {isShare || !showGraphWidget ? '' : (
        <div className={`report-section ${styles.graphSection}`}>
          <h3>{t('Graph')}</h3>
          <GraphWidget
            title={t('Topic graph')}
            description={t('Explore the structure of actor connections in this topic. Find communities, key spreaders, and bridges between them.')}
            generatingHint={t("Usually takes 3–20 minutes. You can leave this page, we'll notify you when it's ready.")}
            status={graphWidget.status}
            generatedAt={graphWidget.generatedAt}
            stats={graphWidget.stats}
            isBusy={graphWidget.isBusy}
            showDateRange={false}
            entityName={story?.title}
            graphName={graphWidget.graphName}
            graphStartDate={graphWidget.startDate}
            graphEndDate={graphWidget.endDate}
            onGenerate={graphWidget.generate}
            onCancel={graphWidget.cancel}
            onRegenerate={graphWidget.regenerate}
            onOpen={() => graphWidget.graphId && setIsGraphOverlayOpen(true)}
          />
        </div>
      )}

      {isGraphOverlayOpen && graphWidget.graphId && (
        <GraphViewOverlay
          graphId={graphWidget.graphId}
          onClose={() => setIsGraphOverlayOpen(false)}
        />
      )}

      {isAttitude ? (
        <AttitudeBar
          data={sentimentData}
          entity={story}
          entityType={'story'}
          tabList={
            ['messages']
          }
          sourceGroups={sourceGroups}
          countries={countries}
          startDate={startDate}
          endDate={endDate}
          report={report}
        />
      ) : (
        <SentimentBar
          data={sentimentData}
          entity={story}
          entityType={'story'}
          tabList={
            widgetConfig?.sentiment_bar?.length < 2
              ? widgetConfig?.sentiment_bar
              : ['messages', 'posts']
          }
          sourceGroups={sourceGroups}
          countries={countries}
          startDate={startDate}
          endDate={endDate}
          report={report}
          isShare={isShare}
        />
      )}

      {!(
        report?.type === 'EVENTS_ONLY' ||
        report?.type === 'EVENTS_AND_OPINIONS_V2' ||
        report?.type === 'EVENTS_ONLY_BETA'
      ) ? (
        <OpinionsWidget
          report={report}
          story={story}
          isShare={isShare}
          sourceGroups={sourceGroups}
          countries={countries}
          countryGroups={countryGroups}
          startDate={startDate}
          endDate={endDate}
          viewMode={widgetConfig?.opinion_card || 'rating'}
        />
      ) : null}

      {Object.keys(widgetConfig).length === 0 || widgetConfig?.geo_widget ? (
        <div className="report-section">
          <h3>{t('Geography')}</h3>
          <GeoChart
            report={report}
            story={story}
            sourceGroups={sourceGroups}
            countries={countries}
            countryGroups={countryGroups}
            startDate={startDate}
            endDate={endDate}
            isShare={isShare}
          />
        </div>
      ) : (
        ''
      )}

      <TopActorsWidget
        report={report}
        story={story}
        isShare={isShare}
        sourceGroups={sourceGroups}
        countries={countries}
        countryGroups={countryGroups}
        startDate={startDate}
        endDate={endDate}
        isAttitude={isAttitude}
      />

      {Object.keys(widgetConfig).length === 0 || widgetConfig?.actor_types ? (
        <ActorsTypes
          report={report}
          story={story}
          sourceGroups={sourceGroups}
          countries={countries}
          countryGroups={countryGroups}
          startDate={startDate}
          endDate={endDate}
          isShare={isShare}
        />
      ) : (
        ''
      )}

      {Object.keys(widgetConfig).length === 0 ||
      widgetConfig?.evolution_chart ? (
        <EvolutionChart
          data={storyShares}
          narrative={report}
          story={story}
          isShare={isShare}
          chartTab={widgetConfig?.evolution_tabs ? widgetConfig?.evolution_tabs[0] : 'publications'}
          onChange={(aggregation, breakdown) => {
            handleEvolutionChartChange(aggregation?.value, breakdown?.value);
          }}
          tabList={
            widgetConfig?.evolution_tabs || [
              'publications',
              'impressions',
              'engagement',
            ]
          }
          filtersSettings={
            widgetConfig?.evolution_breakdowns || ['aggregation', 'breakdown']
          }
        />
      ) : (
        ''
      )}

      <Modal
        isVisible={isSaveCaseModalOpen}
        title={t('Save as case')}
        onClose={() => setIsSaveCaseModalOpen(false)}
        content={
          <div className={styles.saveCaseModalContent}>
            <p>{t('Are you sure you want to save this topic as a new manual case?')}</p>
            {hasFilters && (
              <div className={styles.filtersWarning} role="alert">
                <WarningIcon className={styles.filtersWarningIcon} aria-hidden="true" />
                <span>{t('You were viewing this topic with filters applied. The case may contain more publications, as it will be saved with the unfiltered topic data.')}</span>
              </div>
            )}
          </div>
        }
        footer={
          <div className={styles.saveCaseModalFooter}>
            <button
              className="btn-reset"
              onClick={() => setIsSaveCaseModalOpen(false)}
            >
              {t('Cancel')}
            </button>
            <button
              className="btn-primary"
              onClick={() => {
                setIsSaveCaseModalOpen(false);
                handleCreateCase();
              }}
            >
              {t('Create case')}
            </button>
          </div>
        }
      />

      {isThreatModalOpen && (
        <ThreatEditModal
          isOpen={isThreatModalOpen}
          title={t('Save as threat')}
          threat={story}
          message={hasFilters ? t('You were viewing this topic with filters applied. The threat may contain more publications, as it will be saved with the unfiltered topic data.') : null}
          onChange={() => setIsThreatModalOpen(false)}
          create={true}
          isAdmin={currentUser?.is_super_admin}
          handleThreat={({
            threat,
            threatName,
            threatDescription,
            caseType,
            incidentType,
            topics,
            geoScope,
            narratives,
            campaigns,
            counterEntities,
            reviewStatus,
            declineReason,
            organizations,
            isCreatedByUcbiReporter,
            threatPublicNameEn,
            threatPublicNameUk,
            threatPublicDescriptionEn,
            threatPublicDescriptionUk,
            threatIsPublic,
          }) =>
            saveToThreat(
              report.id,
              threatName,
              threatDescription,
              caseType,
              incidentType,
              topics,
              geoScope,
              narratives,
              campaigns,
              counterEntities,
              reviewStatus,
              declineReason,
              organizations,
              isCreatedByUcbiReporter,
              threatPublicNameEn,
              threatPublicNameUk,
              threatPublicDescriptionEn,
              threatPublicDescriptionUk,
              threatIsPublic,
              {[story.id]: true},
            )
          }
        />
      )}
    </div>
  );
};
